import { type PointShapeType } from '../../../shape/type.js';

const TRIANGLE_RADIUS_ADJUSTMENT = 0.5;

/**
 * Draws a geometric symbol (circle, square, triangle, or diamond) on a canvas context.
 *
 * @param ctx - The CanvasRenderingContext2D to draw on.
 * @param cx - The x-coordinate of the center of the symbol.
 * @param cy - The y-coordinate of the center of the symbol.
 * @param radius - The radius (or half-size) of the symbol.
 * @param shape - The shape to draw. One of 'circle', 'square', 'triangle', or 'diamond'.
 *
 * @remarks
 * - For circles, the radius is the actual radius.
 * - For squares, the radius is half the side length.
 * - For triangles and diamonds, the radius controls the overall size.
 */
export function drawSymbol(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  radius: number,
  shape: PointShapeType = 'circle',
) {
  switch (shape) {
    case 'circle':
      ctx.arc(cx, cy, radius, 0, 2 * Math.PI);
      break;
    case 'square': {
      const s = radius;
      ctx.rect(cx - s, cy - s, 2 * s, 2 * s);
      break;
    }
    case 'triangle': {
      const r = radius - TRIANGLE_RADIUS_ADJUSTMENT;
      const R = 2 * r;
      const side = r * 2 * Math.sqrt(3);

      ctx.moveTo(cx, cy - R);
      ctx.lineTo(cx + side / 2, cy + r);
      ctx.lineTo(cx - side / 2, cy + r);
      ctx.closePath();
      break;
    }
    case 'diamond': {
      const s = radius * Math.sqrt(2);
      ctx.moveTo(cx, cy - s);
      ctx.lineTo(cx + s, cy);
      ctx.lineTo(cx, cy + s);
      ctx.lineTo(cx - s, cy);
      ctx.closePath();
      break;
    }
    default:
      ctx.arc(cx, cy, radius, 0, 2 * Math.PI);
  }
}
